/**
 * Native Mapbox (`@rnmapbox/maps`) looks up the Fabric view tag after JS
 * already called `setHandledMapChangedEvents`. iOS polls for 10s; if the
 * screen unmounted (explore preview under a pushed vehicle, tab blur) the
 * tag is gone and JS sees an unhandled rejection:
 * "Could not find view with tag N in setHandledMapChangedEvents after 53
 * attempts over 10029ms" (TAGO-MOBILE-47).
 *
 * Root: do not mount or command the native map unless the screen is focused
 * and has a real layout; cancel JS-side native commands on unmount. Native
 * polling cannot be aborted from JS — the library patch swallows the late
 * rejection only when the JS map is already gone.
 */

const MISSING_NATIVE_MAP_VIEW =
  /Could not find view with tag\s+\d+\s+in\s+\S+\s+after\s+\d+\s+attempts over\s+[\d.]+ms/i

export function shouldMountNativeMap(input: {
  screenFocused: boolean
  layoutReady: boolean
}): boolean {
  return input.screenFocused && input.layoutReady
}

export function shouldArmDeferredNativeMap(input: {
  screenFocused: boolean
  cancelled: boolean
}): boolean {
  return input.screenFocused && !input.cancelled
}

export function isMissingNativeMapViewError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : ''
  return MISSING_NATIVE_MAP_VIEW.test(message)
}

/**
 * A missing-tag rejection after JS unmounted is a cancelled command, not a
 * product defect. The same rejection while JS still considers the map mounted
 * is a real failure and must still reach Sentry.
 */
export function shouldSurfaceMissingNativeMapView(input: {
  jsMounted: boolean
  err: unknown
}): boolean {
  if (!isMissingNativeMapViewError(input.err)) return true
  return input.jsMounted
}

export function createNativeMapCommandGate() {
  let mounted = true
  return {
    isMounted(): boolean {
      return mounted
    },
    dispose(): void {
      mounted = false
    },
    run<T>(command: () => T): T | undefined {
      if (!mounted) return undefined
      return command()
    },
  }
}
